/**
 * Comprehensive input validation utilities for React components
 * Supports: text, email, phone, file, select, radio, checkbox, number, date, etc.
 */

// ========== FILE VALIDATION ==========
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ========== REGEX PATTERNS ==========
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^\+?[0-9]{10,15}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_REGEX = /^[0-9]{5,17}$/;
const NAME_REGEX = /^[a-zA-Z\s\-']{2,}$/;

// ========== FILE VALIDATORS ==========

/**
 * Validates a single file for extension and size
 * @param {File} file - The file object to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateFile = (file) => {
  if (!file) {
    return { isValid: true, error: '' };
  }

  const fileName = file.name.toLowerCase();
  const fileExt = fileName.substring(fileName.lastIndexOf('.'));

  if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`,
    };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      isValid: false,
      error: `File size (${fileSizeMB}MB) exceeds ${MAX_FILE_SIZE_MB}MB limit`,
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates multiple files
 * @param {FileList|Array} files - Array of file objects
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateFiles = (files) => {
  const errors = [];

  if (!files || files.length === 0) {
    return { isValid: true, errors: [] };
  }

  Array.from(files).forEach((file, index) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      errors.push(`File ${index + 1} (${file.name}): ${validation.error}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// ========== TEXT VALIDATORS ==========

/**
 * Validates required text field is not empty
 * @param {string} value - The text value
 * @returns {string} Error message or empty string
 */
export const validateRequired = (value) => {
  if (!value || value.trim() === '') {
    return 'This field is required.';
  }
  return '';
};

/**
 * Validates minimum length
 * @param {string} value - The text value
 * @param {number} minLen - Minimum length
 * @returns {string} Error message or empty string
 */
export const validateMinLength = (value, minLen) => {
  if (value && value.trim().length < minLen) {
    return `Minimum ${minLen} characters required.`;
  }
  return '';
};

/**
 * Validates maximum length
 * @param {string} value - The text value
 * @param {number} maxLen - Maximum length
 * @returns {string} Error message or empty string
 */
export const validateMaxLength = (value, maxLen) => {
  if (value && value.trim().length > maxLen) {
    return `Maximum ${maxLen} characters allowed.`;
  }
  return '';
};

/**
 * Validates name field (letters, spaces, hyphens, apostrophes)
 * @param {string} value - The name value
 * @returns {string} Error message or empty string
 */
export const validateName = (value) => {
  if (!value) return '';
  
  if (!NAME_REGEX.test(value.trim())) {
    return 'Name should contain only letters, spaces, hyphens, and apostrophes.';
  }
  return '';
};

// ========== EMAIL & PHONE VALIDATORS ==========

/**
 * Validates email format
 * @param {string} value - The email value
 * @returns {string} Error message or empty string
 */
export const validateEmail = (value) => {
  if (!value) return '';
  
  if (!EMAIL_REGEX.test(value.trim())) {
    return 'Please enter a valid email address.';
  }
  return '';
};

/**
 * Validates phone number (10-15 digits)
 * @param {string} value - The phone value
 * @returns {string} Error message or empty string
 */
export const validatePhone = (value) => {
  if (!value) return '';
  
  const cleaned = value.replace(/[\s\-\(\)\.+]/g, '');
  
  if (!PHONE_REGEX.test(cleaned)) {
    return 'Phone number must be 10-15 digits.';
  }
  return '';
};

// ========== FINANCIAL VALIDATORS ==========

/**
 * Validates PAN format
 * @param {string} value - The PAN value
 * @returns {string} Error message or empty string
 */
export const validatePAN = (value) => {
  if (!value) return '';
  
  if (!PAN_REGEX.test(value.toUpperCase().trim())) {
    return 'Invalid PAN format (e.g., AAAAA0000A).';
  }
  return '';
};

/**
 * Validates IFSC code
 * @param {string} value - The IFSC value
 * @returns {string} Error message or empty string
 */
export const validateIFSC = (value) => {
  if (!value) return '';
  
  if (!IFSC_REGEX.test(value.toUpperCase().trim())) {
    return 'Invalid IFSC code (e.g., SBIN0001234).';
  }
  return '';
};

/**
 * Validates bank account number
 * @param {string} value - The account value
 * @returns {string} Error message or empty string
 */
export const validateBankAccount = (value) => {
  if (!value) return '';
  
  if (!ACCOUNT_REGEX.test(value.trim())) {
    return 'Invalid account number (5-17 digits).';
  }
  return '';
};

// ========== NUMERIC VALIDATORS ==========

/**
 * Validates positive number
 * @param {number|string} value - The number value
 * @returns {string} Error message or empty string
 */
export const validatePositiveNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const num = parseFloat(value);
  if (isNaN(num)) {
    return 'Please enter a valid number.';
  }
  if (num <= 0) {
    return 'Value must be greater than 0.';
  }
  return '';
};

/**
 * Validates percentage (0-100)
 * @param {number|string} value - The percentage value
 * @returns {string} Error message or empty string
 */
export const validatePercentage = (value) => {
  if (!value && value !== 0) return '';
  
  const num = parseFloat(value);
  if (isNaN(num)) {
    return 'Please enter a valid percentage.';
  }
  if (num < 0 || num > 100) {
    return 'Percentage must be between 0 and 100.';
  }
  return '';
};

// ========== SELECT/CHOICE VALIDATORS ==========

/**
 * Validates that a select/choice field has a value selected
 * @param {string} value - The selected value
 * @returns {string} Error message or empty string
 */
export const validateSelect = (value) => {
  if (!value || value === '' || value === 'null') {
    return 'Please select an option.';
  }
  return '';
};

/**
 * Validates that a radio button is selected
 * @param {string} value - The selected value
 * @returns {string} Error message or empty string
 */
export const validateRadio = (value) => {
  if (!value || value === '' || value === 'null') {
    return 'Please select an option.';
  }
  return '';
};

// ========== CHECKBOX VALIDATORS ==========

/**
 * Validates that a checkbox is checked
 * @param {boolean} value - The checkbox value
 * @returns {string} Error message or empty string
 */
export const validateCheckbox = (value) => {
  if (!value || value === 'false' || value === false) {
    return 'This field must be checked.';
  }
  return '';
};

/**
 * Validates that at least one checkbox is selected
 * @param {Array} values - Array of selected values
 * @returns {string} Error message or empty string
 */
export const validateAtLeastOneCheckbox = (values) => {
  if (!values || values.length === 0) {
    return 'Please select at least one option.';
  }
  return '';
};

/**
 * Validates maximum checkbox selections
 * @param {Array} values - Array of selected values
 * @param {number} maxCount - Maximum allowed selections
 * @returns {string} Error message or empty string
 */
export const validateMaxCheckboxes = (values, maxCount) => {
  if (values && values.length > maxCount) {
    return `Maximum ${maxCount} selections allowed.`;
  }
  return '';
};

// ========== DATE VALIDATORS ==========

/**
 * Validates date is in the past
 * @param {string} value - The date value (YYYY-MM-DD)
 * @returns {string} Error message or empty string
 */
export const validatePastDate = (value) => {
  if (!value) return '';
  
  const date = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (date >= today) {
    return 'Date must be in the past.';
  }
  return '';
};

/**
 * Validates date is in the future
 * @param {string} value - The date value (YYYY-MM-DD)
 * @returns {string} Error message or empty string
 */
export const validateFutureDate = (value) => {
  if (!value) return '';
  
  const date = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (date <= today) {
    return 'Date must be in the future.';
  }
  return '';
};

// ========== UTILITY FUNCTIONS ==========

export const getFileConstraintMessage = () => {
  return `Allowed: PDF, JPG, PNG. Max: ${MAX_FILE_SIZE_MB}MB per file.`;
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const handleFileInputChange = (event, multiple = false, setFiles, setError) => {
  const files = event.target.files;

  if (multiple) {
    const validation = validateFiles(files);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      setFiles([]);
    } else {
      setError('');
      setFiles(Array.from(files));
    }
  } else {
    const file = files[0];
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.error);
      setFiles(null);
    } else {
      setError('');
      setFiles(file);
    }
  }
};

